import * as Sentry from '@sentry/react'
import { IS_IOS_APP } from './services/runtime'

/**
 * Browser-side Sentry setup — the frontend twin of backend/src/instrument.js.
 *
 * Imported first in main.jsx, before App, so Sentry is already watching
 * before any component renders. If VITE_SENTRY_DSN isn't set (local dev, or
 * a build made before the key existed on Render) this quietly does nothing
 * rather than erroring — same contract as the backend.
 *
 * Worth knowing: VITE_ variables are baked into the bundle at BUILD time,
 * not read at runtime. The key has to be present on Render when `vite build`
 * runs — setting it afterwards and restarting the service does nothing until
 * the next deploy.
 */

const dsn = import.meta.env.VITE_SENTRY_DSN

// Keys that must never leave the browser inside a report. Deliberately the
// same list as SENSITIVE_KEYS in backend/src/instrument.js — if you add one
// there, add it here.
const SENSITIVE_KEYS = /pass|secret|token|jwt|authorization|cookie|api[-_]?key|ssn|card|cvv/i

function scrub(value) {
  if (Array.isArray(value)) return value.map(scrub)
  if (value && typeof value === 'object') {
    const cleaned = {}
    for (const [key, val] of Object.entries(value)) {
      cleaned[key] = SENSITIVE_KEYS.test(key) ? '[Redacted]' : scrub(val)
    }
    return cleaned
  }
  return value
}

// Strip ?token=... style secrets out of any URL before it is reported. Login
// and password-reset links carry them in the query string, and those URLs
// show up in breadcrumbs for every request made before a crash.
function scrubUrl(url) {
  if (typeof url !== 'string') return url
  return url.replace(/([?&][^=&]*(?:token|key|secret|jwt|code)[^=&]*=)[^&]*/gi, '$1[Redacted]')
}

Sentry.init({
  dsn,

  // No DSN means no reporting, and the SDK stays inert rather than throwing.
  enabled: Boolean(dsn),

  environment: import.meta.env.MODE,

  // Set from RENDER_GIT_COMMIT at build time — see vite.config.js. Lets an
  // error be traced to the exact deploy that introduced it, which is the
  // whole point of shipping several times a day.
  release: import.meta.env.VITE_SENTRY_RELEASE,

  // Distinguishes a real iOS device/simulator report from a browser one at
  // a glance in the Sentry issue list — "runtime" per services/runtime.js's
  // own naming rule (never "platform", which means ESPN/Yahoo/Sleeper
  // elsewhere in this codebase). appVersion doubles as the build identifier
  // (see vite.config.js's `release` const — RENDER_GIT_COMMIT at build
  // time), the same value already used for Sentry's own `release` field
  // above, so a native event's tag and its release always agree.
  initialScope: {
    tags: {
      runtime: IS_IOS_APP ? 'ios-native' : 'web',
      appVersion: import.meta.env.VITE_SENTRY_RELEASE,
    },
  },

  // Never attach IP addresses or cookies to a report.
  sendDefaultPii: false,

  // Performance tracing on 10% of page loads — enough to catch a slow API
  // route during draft week without burning the quota on ordinary traffic.
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,

  // Only trace calls to our own API. Adding third-party hosts here would
  // attach our trace headers to their requests and trip their CORS checks.
  tracePropagationTargets: [/^\//, /^https:\/\/play-jai\.com/],

  // Noise that is not a JAI bug and would otherwise dominate the issue list.
  // "Failed to fetch" is overwhelmingly a user going offline or closing the
  // tab mid-request — a genuine backend outage is caught by the uptime
  // monitor on /api/health instead, which is a far more reliable signal.
  ignoreErrors: [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Non-Error promise rejection captured',
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
    'Load failed',
    'AbortError',
  ],

  // Browser extensions throw constantly inside other people's pages and it
  // is never our code. Safe to deny even though JAI ships its own ESPN
  // extension: that runs in its own context and reports separately.
  denyUrls: [
    /extensions\//i,
    /^chrome-extension:\/\//i,
    /^moz-extension:\/\//i,
    /^safari-web-extension:\/\//i,
  ],

  beforeSend(event) {
    if (event.request) {
      if (event.request.url) event.request.url = scrubUrl(event.request.url)
      event.request = scrub(event.request)
    }
    if (event.extra) event.extra = scrub(event.extra)
    return event
  },

  // Breadcrumbs log every fetch the user made in the lead-up to a crash, so
  // they need the same URL scrubbing as the event itself.
  beforeBreadcrumb(crumb) {
    if (crumb?.data?.url) crumb.data.url = scrubUrl(crumb.data.url)
    return crumb
  },
})
